import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  Query,
  query,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { ObjectHelper } from "../helpers/object.helper";
import { SubcontractRequest } from "../models/subcontract-request.model";
import { Functions, httpsCallable, HttpsCallableResult } from "@angular/fire/functions";
import { SubcontractRequestState } from "../enums/subcontract-request-state.enum";
import { ProductPrice } from "../models/product-order.model";

@Injectable({
  providedIn: 'root',
})
export class SubcontractRequestService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);

  /**
   * Subscribe to subcontract requests collection
   * @param organizationId Current organization Id
   * @param externalProductionId External production id
   * @returns SubcontractRequest subscription query
   */
  list(organizationId: string, externalProductionId: string): Query<SubcontractRequest> {
    return query(collection(this.firestore, 'subcontract-requests'),
      where('organizationId', '==', organizationId),
      where('externalProductionId', '==', externalProductionId)).withConverter(SubcontractRequest.converter);
  }

  /**
   * Subscribe to subcontract requests collection
   * @param organizationId Current organization Id
   * @param externalProductionId External production id
   * @returns SubcontractRequest subscription query
   */
  async listSync(organizationId: string, externalProductionId: string)
    : Promise<SubcontractRequest[]> {
    const q = this.list(organizationId, externalProductionId);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Subscribe to subcontract requests by subcontractor
   * @param subContractorEmail Current organization Id
   * @returns SubcontractRequest subscription query
   */
  async externalProductionBySubcontractorEmail(subContractorEmail: string): Promise<SubcontractRequest[]> {
    const q =  query(collection(this.firestore, 'subcontract-requests'),
      where('subcontractorEmails', 'array-contains', subContractorEmail),
      where('state', 'in', [SubcontractRequestState.WAITING, SubcontractRequestState.ACCEPTED])
    ).withConverter(SubcontractRequest.converter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Create a subcontract request
   * @param subcontractRequest The subcontract request to save
   * @returns Id of the created subcontract request
   */
  async create(subcontractRequest: SubcontractRequest): Promise<string> {
    const ref = collection(this.firestore, 'subcontract-requests').withConverter(SubcontractRequest.converter);
    const created = await addDoc(ref, subcontractRequest);
    return created.id;
  }

  /**
   * Update a subcontract request
   * @param id The request id
   * @param subcontractRequest The subcontract request to save
   */
  async update(id: string, subcontractRequest: Partial<SubcontractRequest>): Promise<void> {
    const ref = doc(this.firestore, 'subcontract-requests', id)
      .withConverter(SubcontractRequest.converter);
    await updateDoc(ref, ObjectHelper.anonymize(subcontractRequest));
  }

  /**
   * Get a subcontract request by id
   * @param id The subcontract request id
   * @returns The subcontract request or null
   */
  async get(id: string): Promise<SubcontractRequest | null> {
    const ref = doc(this.firestore, 'subcontract-requests', id).withConverter(SubcontractRequest.converter);
    const document = await getDoc(ref);
    return document.data();
  }

  /**
   * Delete a subcontract request
   * @param request The subcontract request
   */
  async delete(request: SubcontractRequest): Promise<void> {
    const ref = doc(this.firestore, 'subcontract-requests', request.id)
      .withConverter(SubcontractRequest.converter);
    await deleteDoc(ref);
  }

  /**
   * Delete a subcontract request by external production id
   * @param organizationId Id of the organization
   * @param externalProductionId Id of the external production
   */
  async deleteByExternalProduction
  (organizationId: string, externalProductionId: string): Promise<void> {
    const ref = query(collection(this.firestore, 'subcontract-requests'),
      where('organizationId', '==', organizationId),
      where('externalProductionId', '==', externalProductionId)).withConverter(SubcontractRequest.converter);
    const snapshot = await getDocs(ref);
    if (snapshot.empty) {
      return;
    }
    for (const document of snapshot.docs) {
      await deleteDoc(document.ref);
    }
  }

  /**
   * Get subcontract requests by external production id
   * @param organizationId Id of the organization
   * @param externalProductionId Id of the external production
   * @param subcontractorIds Subcontractor ids
   * @returns SubcontractRequest array or an empty array
   */
  async getByExternalProduction
  (organizationId: string, externalProductionId: string, subcontractorIds?: string[])
    : Promise<SubcontractRequest[] | null> {
    if (subcontractorIds && !subcontractorIds.length) {
      return [];
    }
    const whereClauses = [
      where('organizationId', '==', organizationId),
      where('externalProductionId', '==', externalProductionId)
    ]
    if (subcontractorIds) {
      whereClauses.push(where('subcontractorId', 'in', subcontractorIds))
    }
    const q = query(collection(this.firestore, 'subcontract-requests'), ...whereClauses)
      .withConverter(SubcontractRequest.converter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(document => document.data());
  }

  /**
   * Resend the request email for a single subcontract request.
   * @param subContractorRequestId The subcontract request id
   * @returns The promise
   */
  public resendRequest(subContractorRequestId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ subContractorRequestId: string }, void>(
      this.functions, 'resend_request_gen2')({ subContractorRequestId });
  }

  /**
   * Send acceptation email
   * @param subContractorRequestId The subcontractor request id
   * @param response The response
   * @param comment The comment
   * @param prices The subcontractor prices
   * @returns The promise
   */
  public sendAcceptation(subContractorRequestId: string, response: 'accept' | 'refuse', comment: string,
    prices: Record<string, ProductPrice[]>)
    : Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ subContractorRequestId: string, response: 'accept' | 'refuse', comment: string,
      prices: Record<string, ProductPrice[]> }, void>(
        this.functions, 'send_acceptation_gen2')({ subContractorRequestId, response, comment, prices });
  }

  /**
   * Retrieves and returns a list of subcontract requests associated with the user's organizations.
   * This method aggregates subcontract requests for all organizations where the user has either user or admin access.
   * @param subcontractorEmail The email of the subcontractor.
   * @returns A promise resolving to an array of subcontract requests.
   */
  async getUserRequests(subcontractorEmail: string): Promise<SubcontractRequest[]> {
    return this.externalProductionBySubcontractorEmail(subcontractorEmail);
  }
}
